const s = (v) => (v === null || v === undefined ? '' : String(v));
export const trimmed = (v) => s(v).trim();

/* ── Sanitisers (as-you-type) ────────────────────────────────────────────── */

export const digitsOnly = (v, max) => {
  const d = s(v).replace(/\D/g, '');
  return max ? d.slice(0, max) : d;
};

export const capLength = (v, max) => s(v).slice(0, max);

/* ── 1. Staff ID — at most 10 characters ─────────────────────────────────── */

export const STAFF_ID_MAX = 10;
export const sanitiseStaffId = (v) => digitsOnly(v, STAFF_ID_MAX);

export function checkStaffId(v, { required = true } = {}) {
  const val = trimmed(v);
  if (!val) return required ? 'Staff ID is required' : '';
  if (!/^\d+$/.test(val)) return 'Staff ID can only contain numbers';
  if (val.length > STAFF_ID_MAX) return `Staff ID cannot exceed ${STAFF_ID_MAX} digits`;
  return '';
}

/* ── 2. EPF No — at most 5 characters ────────────────────────────────────── */

export const EPF_MAX = 5;
export const sanitiseEpf = (v) => capLength(v, EPF_MAX);

export function checkEpf(v, { required = false } = {}) {
  const val = trimmed(v);
  if (!val) return required ? 'EPF No is required' : '';
  if (val.length > EPF_MAX) return `EPF No cannot exceed ${EPF_MAX} characters`;
  return '';
}

/* ── OT Rate — non-negative numeric, up to 2 decimal places ──────────── */

export function sanitiseOtRate(v) {
  // Digits + at most one decimal point. Negative signs and letters are dropped.
  let out = s(v).replace(/[^\d.]/g, '');
  const parts = out.split('.');
  if (parts.length > 2) out = parts[0] + '.' + parts.slice(1).join('');
  return out;
}

export function checkOtRate(v, { required = false } = {}) {
  const val = trimmed(v);
  if (!val) return required ? 'OT rate is required' : '';
  if (!/^\d+(\.\d+)?$/.test(val)) return 'OT rate must be a valid number';
  if (Number(val) < 0) return 'OT rate cannot be negative';
  return '';
}


/* ── 12. Initials — always uppercase ────────────────────────────────────── */

export const sanitiseInitials = (v) => s(v).toUpperCase();


export const NIC_OLD_LENGTH = 10;   // 9 digits + V
export const NIC_NEW_MAX = 12;      // digits only

/**
 * Keeps only digits and a single trailing V, upper-cased. A V can only ever
 * sit at the end, and once it is there the number is capped at 10 characters.
 */
export function sanitiseNic(v) {
  const raw = s(v).toUpperCase().replace(/[^0-9V]/g, '');
  const digits = raw.replace(/V/g, '');
  // A V anywhere in what was typed means the user is entering the old format.
  if (raw.includes('V')) return `${digits.slice(0, NIC_OLD_LENGTH - 1)}V`;
  return digits.slice(0, NIC_NEW_MAX);
}

/** What goes in the payload: always an upper-case V. */
export const normaliseNic = (v) => sanitiseNic(v);

export function checkNic(v, { required = true } = {}) {
  const val = normaliseNic(v);
  if (!val) return required ? 'NIC No is required' : '';

  if (val.includes('V')) {
    if (!/^\d{9}V$/.test(val)) {
      return `NIC ending in V must be exactly ${NIC_OLD_LENGTH} characters — 9 digits then V`;
    }
    return '';
  }

  if (!/^\d+$/.test(val)) return 'NIC can only contain numbers, or 9 numbers followed by V';
  if (val.length > NIC_NEW_MAX) return `NIC cannot exceed ${NIC_NEW_MAX} numbers`;
  return '';
}

/* ── Dates ───────────────────────────────────────────────────────────────── */

const ISO_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Accepts ISO and the API's .NET dates, and always hands back yyyy-mm-dd. */
export function toIso(v) {
  const val = trimmed(v);
  if (!val) return '';
  if (ISO_RE.test(val)) return val;

  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(val);
  if (m) {
    const [, a, b, y] = m;
    const day = Number(a) > 12 ? a : b;
    const month = Number(a) > 12 ? b : a;
    return `${y}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const parseIso = (v) => {
  const iso = toIso(v);
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  // Rejects 2026-02-31 and friends, which Date would silently roll over.
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return null;
  return date;
};

export const todayIso = () => toIso(new Date());

/** Whole years between a date and today. */
export function ageOn(dob, today = new Date()) {
  const d = parseIso(dob);
  if (!d) return null;
  let years = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) years -= 1;
  return years;
}

/* ── 4. Date of birth — yyyy-mm-dd, 18 or older today ────────────────────── */

export const MIN_AGE = 18;

export function checkDateOfBirth(v, { required = true, today = new Date() } = {}) {
  const val = trimmed(v);
  if (!val) return required ? 'Date of birth is required' : '';
  const d = parseIso(val);
  if (!d) return 'Date of birth must be a valid date in yyyy-mm-dd format';
  if (d > today) return 'Date of birth cannot be in the future';
  const age = ageOn(val, today);
  if (age < MIN_AGE) return `Employee must be at least ${MIN_AGE} years old (currently ${age})`;
  return '';
}

/** The latest DOB that still satisfies the age rule — for the picker's max. */
export function maxDobIso(today = new Date()) {
  const d = new Date(today.getFullYear() - MIN_AGE, today.getMonth(), today.getDate());
  return toIso(d);
}

/* ── 8. Date of joining — yyyy-mm-dd, never in the future ────────────────── */

export function checkDateOfJoining(v, { required = true, today = new Date() } = {}) {
  const val = trimmed(v);
  if (!val) return required ? 'Date of joining is required' : '';
  const d = parseIso(val);
  if (!d) return 'Date of joining must be a valid date in yyyy-mm-dd format';
  const end = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
  if (d > end) return 'Date of joining cannot be a future date';
  return '';
}

/* ── 5. Phone numbers — digits only, exactly up to 10 ────────────────────── */

export const PHONE_MAX = 10;
export const sanitisePhone = (v) => digitsOnly(v, PHONE_MAX);

export function checkPhone(v, label = 'Phone number', { required = false } = {}) {
  const val = trimmed(v);
  if (!val) return required ? `${label} is required` : '';
  if (!/^\d+$/.test(val)) return `${label} can only contain numbers`;
  if (val.length > PHONE_MAX) return `${label} cannot exceed ${PHONE_MAX} numbers`;
  return '';
}

/* ── 6. Email — must contain @ ───────────────────────────────────────────── */

export function checkEmail(v, label = 'Email', { required = false } = {}) {
  const val = trimmed(v);
  if (!val) return required ? `${label} is required` : '';
  if (!val.includes('@')) return `${label} must contain @`;
  return '';
}

/* ── 7. Postal code — exactly 5 digits ───────────────────────────────────── */

export const POSTAL_LENGTH = 5;
export const sanitisePostal = (v) => digitsOnly(v, POSTAL_LENGTH);

export function checkPostal(v, { required = false } = {}) {
  const val = trimmed(v);
  if (!val) return required ? 'Postal code is required' : '';
  if (!/^\d{5}$/.test(val)) return `Postal code must be exactly ${POSTAL_LENGTH} numbers`;
  return '';
}

/* ── 11. Bank account number — digits only ──────────────────────────────── */

export const ACCOUNT_NUMBER_MAX = 20;
export const sanitiseAccountNumber = (v) => digitsOnly(v, ACCOUNT_NUMBER_MAX);

export function checkAccountNumber(v, { required = false } = {}) {
  const val = trimmed(v);
  if (!val) return required ? 'Account number is required' : '';
  if (!/^\d+$/.test(val)) return 'Account number can only contain digits';
  if (val.length > ACCOUNT_NUMBER_MAX) return `Account number cannot exceed ${ACCOUNT_NUMBER_MAX} digits`;
  return '';
}

/* ── 9. Total experience — years and months, sent as "1Y2M" ──────────────── */

/** "1Y2M" / "2Y" / "6M" / legacy "5 years" → { years, months }. */
export function parseExperience(v) {
  const val = trimmed(v).toUpperCase();
  if (!val) return { years: '', months: '' };

  const compact = /^(?:(\d{1,2})Y)?(?:(\d{1,2})M)?$/.exec(val.replace(/\s+/g, ''));
  if (compact && (compact[1] || compact[2])) {
    return { years: compact[1] || '', months: compact[2] || '' };
  }

  // Anything older and free-text: pull the first number out as years.
  const loose = /(\d{1,2})\s*(?:Y|YR|YRS|YEAR|YEARS)?/.exec(val);
  return { years: loose ? loose[1] : '', months: '' };
}


/* ── 10. GPA — numeric, digits with an optional decimal part ─────────────── */

export function sanitiseGpa(v) {
  let out = s(v).replace(/[^\d.]/g, '');
  const parts = out.split('.');
  if (parts.length > 2) out = parts[0] + '.' + parts.slice(1).join('');
  return out;
}

export function checkGpa(v, { required = false } = {}) {
  const val = trimmed(v);
  if (!val) return required ? 'GPA is required' : '';
  if (!/^\d+(\.\d+)?$/.test(val)) return 'GPA must be a numeric value';
  return '';
}


/** { years, months } → "1Y2M". Empty when both are blank. */
export function formatExperience(years, months) {
  const y = digitsOnly(years, 2);
  const m = digitsOnly(months, 2);
  const yNum = y === '' ? null : Number(y);
  const mNum = m === '' ? null : Number(m);
  let out = '';
  if (yNum !== null && yNum > 0) out += `${yNum}Y`;
  if (mNum !== null && mNum > 0) out += `${mNum}M`;
  // 0 years 0 months is still a real answer, not a blank.
  if (!out && (yNum !== null || mNum !== null)) out = '0Y';
  return out;
}

export function formatExperienceLong(v) {
  const raw = trimmed(v);
  if (!raw) return '';

  const { years, months } = parseExperience(raw);
  const y = years === '' ? null : Number(years);
  const m = months === '' ? null : Number(months);

  // Neither part parsed — show whatever was stored instead of losing it.
  if (y === null && m === null) return raw;

  const parts = [];
  if (y) parts.push(`${y} ${y === 1 ? 'Year' : 'Years'}`);
  if (m) parts.push(`${m} ${m === 1 ? 'Month' : 'Months'}`);

  // A genuine zero is an answer: "0 Years", not an empty string.
  if (!parts.length) return '0 Years';
  return parts.join(' ');
}

export function checkExperience(years, months) {
  const y = trimmed(years);
  const m = trimmed(months);
  if (!y && !m) return '';
  if (y && !/^\d{1,2}$/.test(y)) return 'Years must be a number';
  if (m && !/^\d{1,2}$/.test(m)) return 'Months must be a number';
  if (m && Number(m) > 11) return 'Months must be between 0 and 11';
  return '';
}